import Link from "next/link";
import { JobRow } from "@/components/JobRow";
import { buttonClass } from "@/components/ui";
import type { JobListResult } from "@/lib/queries";

/**
 * The shared job-list card used by the programmatic org/category pages: a
 * server-rendered <ul> of <JobRow>s plus prev/next pagination that appends
 * ?page=N to a fixed base path. Kept deliberately simple — these pages carry a
 * single implicit filter, so they don't need the board's full query-string
 * pagination.
 */
export function JobListPanel({
  result,
  basePath,
  emptyLabel,
  now,
  onPageChange,
}: {
  result: JobListResult;
  basePath: string;
  emptyLabel: string;
  now: Date;
  /**
   * When set, pagination renders as buttons that call this instead of
   * <Link>s — used by PagedJobList so the route itself never has to read
   * `searchParams` (see that component for why). Omit for a plain
   * server-rendered pager.
   */
  onPageChange?: (page: number) => void;
}) {
  const { items, total, page, pageCount } = result;
  const rangeStart = total === 0 ? 0 : (page - 1) * result.pageSize + 1;
  const rangeEnd = Math.min(total, page * result.pageSize);
  const pageHref = (p: number) => (p <= 1 ? basePath : `${basePath}?page=${p}`);

  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <header className="border-b border-neutral-200 px-3 py-2.5 sm:px-4">
        <p className="telex text-[10px] text-neutral-500">
          <strong className="font-semibold text-ink tabular-nums">
            {total.toLocaleString("en-US")}
          </strong>{" "}
          {total === 1 ? "vacancy" : "vacancies"}
          {total > 0 ? (
            <span className="text-neutral-400">
              {" "}
              · showing {rangeStart.toLocaleString("en-US")}–
              {rangeEnd.toLocaleString("en-US")}
            </span>
          ) : null}
        </p>
      </header>

      {items.length > 0 ? (
        <ul className="divide-y divide-neutral-100">
          {items.map((job) => (
            <JobRow key={job.id} job={job} now={now} />
          ))}
        </ul>
      ) : (
        // data-empty is the machine-readable marker the periodic crawl check
        // keys on. Detecting an empty result by matching the visible copy only
        // ever worked for the one page whose wording was hard-coded into the
        // checker — every other caller passes its own emptyLabel and would have
        // silently "passed" while rendering zero results.
        <div className="px-6 py-16 text-center" data-empty="jobs">
          <p className="text-[15px] font-medium text-neutral-900">{emptyLabel}</p>
          <Link href="/jobs" className={`${buttonClass("primary", "sm")} mt-5`}>
            Browse all vacancies
          </Link>
        </div>
      )}

      {pageCount > 1 ? (
        <nav
          aria-label="Pagination"
          className="flex items-center justify-between border-t border-neutral-200 px-3 py-2.5 text-sm sm:px-4"
        >
          {page > 1 ? (
            onPageChange ? (
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                className="rounded-md px-2.5 py-1.5 font-medium text-brand-700 hover:bg-neutral-100"
              >
                ← Previous
              </button>
            ) : (
              <Link
                href={pageHref(page - 1)}
                rel="prev"
                className="rounded-md px-2.5 py-1.5 font-medium text-brand-700 hover:bg-neutral-100"
              >
                ← Previous
              </Link>
            )
          ) : (
            <span className="px-2.5 py-1.5 text-neutral-300">← Previous</span>
          )}
          <span className="font-mono text-[13px] tabular-nums text-neutral-500">
            Page {page} of {pageCount}
          </span>
          {page < pageCount ? (
            onPageChange ? (
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                className="rounded-md px-2.5 py-1.5 font-medium text-brand-700 hover:bg-neutral-100"
              >
                Next →
              </button>
            ) : (
              <Link
                href={pageHref(page + 1)}
                rel="next"
                className="rounded-md px-2.5 py-1.5 font-medium text-brand-700 hover:bg-neutral-100"
              >
                Next →
              </Link>
            )
          ) : (
            <span className="px-2.5 py-1.5 text-neutral-300">Next →</span>
          )}
        </nav>
      ) : null}
    </div>
  );
}
